import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import tableImage from '../../assets/river-zero-table.webp';
import cardbackImage from '../../assets/river-zero-cardback.webp';
import { SEAT_ANCHORS } from '../../../shared/seats';
import type { CardCode } from '../../../shared/engine/holdem';
import type { RoomSnapshot, SeatSnapshot } from '../../../shared/protocol';

type TableSceneProps = {
  snapshot: RoomSnapshot;
  mode: 'lobby' | 'game';
  soundEnabled: boolean;
};

const SUIT_SYMBOLS: Record<string, string> = {
  s: '♠',
  h: '♥',
  d: '♦',
  c: '♣',
};

const formatRank = (rank: string) => (rank === 'T' ? '10' : rank);

const PlayingCard = ({
  card,
  hidden = false,
  compact = false,
}: {
  card?: CardCode;
  hidden?: boolean;
  compact?: boolean;
}) => {
  if (hidden || !card) {
    return (
      <div
        aria-hidden="true"
        className={`playing-card playing-card--back${compact ? ' playing-card--compact' : ''}`}
        style={{ backgroundImage: `url(${cardbackImage})` }}
      />
    );
  }

  const rank = card[0];
  const suit = card[1];
  const suitSymbol = SUIT_SYMBOLS[suit];
  const warmSuit = suit === 'h' || suit === 'd';

  return (
    <div
      className={`playing-card${compact ? ' playing-card--compact' : ''}${
        warmSuit ? ' playing-card--warm' : ''
      }`}
    >
      <span className="playing-card__rank">{formatRank(rank)}</span>
      <span className="playing-card__suit">{suitSymbol}</span>
    </div>
  );
};

const SeatNode = ({
  seatIndex,
  snapshot,
  heroSeat,
  handActive,
}: {
  seatIndex: number;
  snapshot: SeatSnapshot | null;
  heroSeat: number | null;
  handActive: boolean;
}) => {
  const anchor = SEAT_ANCHORS.find((entry) => entry.seat === seatIndex)!;
  const isHero = heroSeat === seatIndex;
  const shouldShowBacks = handActive && !!snapshot && !isHero && snapshot.holeCards.length === 0 && !snapshot.folded;
  const shouldShowRevealedCards = !!snapshot && snapshot.holeCards.length === 2 && !isHero;

  return (
    <div
      className={`seat-node seat-node--${anchor.align}${isHero ? ' seat-node--hero' : ''}${
        snapshot?.isTurn ? ' seat-node--turn' : ''
      }`}
      data-position={anchor.position}
      data-testid={`seat-${seatIndex}`}
      style={{
        left: `${anchor.x}%`,
        top: `${anchor.y}%`,
      }}
    >
      <div className={`seat-tile${snapshot ? '' : ' seat-tile--empty'}`}>
        <div className="seat-tile__head">
          <span className="seat-tile__name">{snapshot?.name ?? `空位 ${seatIndex + 1}`}</span>
          <span className="seat-tile__stack">{snapshot ? snapshot.stack : '空闲'}</span>
        </div>
        <div className="seat-tile__meta">
          <span className="seat-tile__badges">
            {snapshot?.isDealer ? <span className="seat-badge">D</span> : null}
            {snapshot?.isSmallBlind ? <span className="seat-badge">SB</span> : null}
            {snapshot?.isBigBlind ? <span className="seat-badge">BB</span> : null}
          </span>
          <span className="seat-tile__state">
            {snapshot?.folded ? '已弃牌' : snapshot?.allIn ? '全下' : snapshot?.lastActionLabel}
          </span>
        </div>
      </div>
      {shouldShowBacks ? (
        <div className="seat-node__cards">
          <PlayingCard hidden compact />
          <PlayingCard hidden compact />
        </div>
      ) : null}
      {shouldShowRevealedCards ? (
        <div className="seat-node__cards">
          {snapshot.holeCards.map((card) => (
            <PlayingCard card={card} compact key={card} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const WagerNode = ({ seat }: { seat: SeatSnapshot }) => {
  const anchor = SEAT_ANCHORS[seat.seat];
  const x = anchor.x + (50 - anchor.x) * 0.34;
  const y = anchor.y + (50 - anchor.y) * 0.34;
  const style = {
    left: `${x}%`,
    top: `${y}%`,
    '--wager-from-x': `${(anchor.x - x) * 0.42}vw`,
    '--wager-from-y': `${(anchor.y - y) * 0.24}vw`,
  } as CSSProperties;

  return (
    <div
      className="table-wager"
      data-motion="to-pot"
      data-testid={`wager-${seat.seat}`}
      key={`${seat.seat}-${seat.streetBet}`}
      style={style}
    >
      <span className="table-wager__chips" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <strong>{seat.streetBet}</strong>
    </div>
  );
};

export const TableScene = ({ snapshot, mode, soundEnabled }: TableSceneProps) => {
  const sceneRef = useRef<HTMLElement>(null);
  const heroSeat = snapshot.self?.seat ?? null;
  const heroCards = snapshot.self?.holeCards ?? [];
  const handActive =
    snapshot.street !== 'lobby' && snapshot.street !== 'settlement' && snapshot.street !== 'showdown';

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const centerTable = () => {
      if (scene.scrollWidth > scene.clientWidth) {
        scene.scrollLeft = (scene.scrollWidth - scene.clientWidth) / 2;
      }
    };
    centerTable();
    window.addEventListener('resize', centerTable);
    return () => window.removeEventListener('resize', centerTable);
  }, []);

  return (
    <section
      className={`table-scene table-scene--${mode}`}
      data-sound-enabled={soundEnabled ? '1' : '0'}
      ref={sceneRef}
    >
      <div className="table-scene__frame">
        <img
          className="table-scene__image"
          src={tableImage}
          alt="RIVER ZERO 牌桌"
          decoding="async"
          fetchPriority="high"
          width="1280"
          height="720"
        />
        <div className="table-scene__room-strip">
          <span>房间 {snapshot.roomCode}</span>
          <span>
            盲注 {snapshot.blinds.smallBlind}/{snapshot.blinds.bigBlind}
          </span>
          <span>{snapshot.street === 'lobby' ? '等待首局' : `第 ${snapshot.handNumber} 手牌`}</span>
        </div>

        <div className="table-scene__center-stack" data-testid="table-board">
          <div className="table-scene__pot" key={`${snapshot.handNumber}-${snapshot.potTotal}`}>
            <span className="table-scene__pot-label">底池</span>
            <strong>{snapshot.potTotal}</strong>
          </div>
          <div className="table-scene__community">
            {Array.from({ length: 5 }, (_, index) => {
              const card = snapshot.communityCards[index];
              return (
                <div
                  className="table-scene__community-slot"
                  data-reveal={card ? `${index}` : undefined}
                  data-testid={`community-slot-${index}`}
                  key={card ?? index}
                  style={{ '--reveal-delay': `${index * 110}ms` } as CSSProperties}
                >
                  {card ? <PlayingCard card={card} /> : <div className="community-slot__ghost" />}
                </div>
              );
            })}
          </div>
        </div>

        {SEAT_ANCHORS.map((anchor) => (
          <SeatNode
            handActive={handActive}
            heroSeat={heroSeat}
            key={anchor.seat}
            seatIndex={anchor.seat}
            snapshot={snapshot.seats[anchor.seat]}
          />
        ))}

        {snapshot.seats.map((seat) =>
          seat && seat.streetBet > 0 ? <WagerNode key={`${seat.seat}-${seat.streetBet}`} seat={seat} /> : null,
        )}

      </div>

      <div className="hero-cards">
          <div className="hero-cards__label">
            {snapshot.self
              ? `你的手牌 · ${snapshot.seats[snapshot.self.seat]?.name ?? '牌手'} · ${snapshot.seats[snapshot.self.seat]?.stack ?? 0}`
              : '观战位'}
          </div>
          <div className="hero-cards__row">
            {heroCards.length === 2 ? (
              heroCards.map((card) => <PlayingCard card={card} key={card} />)
            ) : handActive ? (
              <>
                <PlayingCard hidden />
                <PlayingCard hidden />
              </>
            ) : (
              <>
                <div className="hero-cards__rest">等待发牌</div>
                <div className="hero-cards__rest hero-cards__rest--subtle">桌面始终保持横向布局</div>
              </>
            )}
          </div>
      </div>
    </section>
  );
};
